import React, { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Reward } from 'src/queries/mappers';
import { Column, DataTable, Pager, SearchField, SortDirection, StatusPill } from './DataTable';
import { Panel } from './Panel';
import { DateRange, isWithin } from './period';
import {
  bodyFont,
  customerName,
  formatCount,
  formatDateTime,
  ink,
  neutral,
  positive,
  surface
} from './tokens';

const PAGE_SIZE = 8;

/** `STREAK_TARGET` is not something to put in front of a merchant. */
const triggerLabel = (triggerType: string) => {
  switch (triggerType) {
    case 'TOTAL_TARGET':
      return 'Full card';
    case 'STREAK_TARGET':
      return 'Streak';
    default:
      return triggerType ? triggerType.replace(/_/g, ' ').toLowerCase() : '—';
  }
};

/**
 * Rewards earned in the selected period, and whether they have been claimed.
 *
 * Reads from the stats payload the rest of the page already holds, so it needs
 * no request of its own.
 */
export const RecentRewardsTable: React.FC<{
  rewards: Reward[];
  range: DateRange;
  rangeLabel: string;
  locationName: string;
}> = ({ rewards, range, rangeLabel, locationName }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('dateCreated');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const inPeriod = useMemo(
    () => rewards.filter((reward) => isWithin(reward.dateCreated, range)),
    [rewards, range]
  );

  const sorted = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? inPeriod.filter((reward) =>
          customerName(reward.firstName, reward.lastName).toLowerCase().includes(query)
        )
      : inPeriod;

    const direction = sortDirection === 'asc' ? 1 : -1;
    const time = (value: string) => {
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return [...filtered].sort((a, b) => {
      if (sortKey === 'customer') {
        return (
          direction *
          customerName(a.firstName, a.lastName).localeCompare(
            customerName(b.firstName, b.lastName)
          )
        );
      }
      if (sortKey === 'dateRedeemed') {
        // Unclaimed rewards have no redemption date; park them at the end
        // whichever way the column is sorted.
        const left = a.redeemed ? time(a.dateRedeemed) : null;
        const right = b.redeemed ? time(b.dateRedeemed) : null;
        if (left === null && right === null) return 0;
        if (left === null) return 1;
        if (right === null) return -1;
        return direction * (left - right);
      }
      return direction * (time(a.dateCreated) - time(b.dateCreated));
    });
  }, [inPeriod, search, sortKey, sortDirection]);

  useEffect(() => {
    setPage(0);
  }, [search, sortKey, sortDirection, range.start, range.end]);

  const outstanding = inPeriod.filter((reward) => !reward.redeemed).length;

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection(key === 'customer' ? 'asc' : 'desc');
  };

  const downloadCsv = () => {
    const rows = [
      ['Reward', 'Customer', 'Earned', 'Redeemed', 'Status', 'Earned for'],
      ...sorted.map((reward) => [
        `${reward.id}`,
        customerName(reward.firstName, reward.lastName),
        reward.dateCreated,
        reward.redeemed ? reward.dateRedeemed : '',
        reward.redeemed ? 'Redeemed' : 'Outstanding',
        triggerLabel(reward.triggerType)
      ])
    ];
    const csv = rows.map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${
      locationName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'location'
    }-rewards.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: Column<Reward>[] = [
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      width: 'minmax(0,1.2fr)',
      render: (reward) => (
        <Typography
          noWrap
          sx={{ fontFamily: bodyFont, fontWeight: 500, fontSize: 14, color: ink.strong }}
        >
          {customerName(reward.firstName, reward.lastName)}
        </Typography>
      )
    },
    {
      key: 'dateCreated',
      label: 'Earned',
      sortable: true,
      width: 'minmax(0,1.2fr)',
      render: (reward) => (
        <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.body }}>
          {formatDateTime(reward.dateCreated)}
        </Typography>
      )
    },
    {
      key: 'dateRedeemed',
      label: 'Redeemed',
      sortable: true,
      width: 'minmax(0,1.2fr)',
      render: (reward) => (
        <Typography
          noWrap
          sx={{
            fontFamily: bodyFont,
            fontSize: 14,
            color: reward.redeemed ? ink.body : ink.faint
          }}
        >
          {reward.redeemed ? formatDateTime(reward.dateRedeemed) : '—'}
        </Typography>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: 'minmax(120px,.8fr)',
      render: (reward) =>
        reward.redeemed ? (
          <StatusPill label='Redeemed' fg={positive.fg} bg={positive.bg} />
        ) : (
          <StatusPill label='Outstanding' fg={neutral.fg} bg={neutral.bg} />
        )
    },
    {
      key: 'trigger',
      label: 'Earned for',
      width: 'minmax(90px,.7fr)',
      render: (reward) => (
        <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.body }}>
          {triggerLabel(reward.triggerType)}
        </Typography>
      )
    }
  ];

  return (
    <Panel
      title='Rewards earned'
      subtitle={
        inPeriod.length
          ? `${formatCount(inPeriod.length)} in ${rangeLabel} · ${formatCount(outstanding)} still to claim`
          : rangeLabel
      }
      hint='Rewards customers earned during the selected period. A reward earned now may be redeemed weeks later, so the redeemed date can fall outside this range.'
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SearchField value={search} onChange={setSearch} placeholder='Search by name' />
          <Tooltip title='Download CSV'>
            <ButtonBase
              aria-label='Download rewards as CSV'
              onClick={downloadCsv}
              disabled={!sorted.length}
              sx={{
                width: 38,
                height: 38,
                borderRadius: '9px',
                border: `1px solid ${surface.borderStrong}`,
                color: ink.body,
                '&:hover': { backgroundColor: surface.grid },
                '&.Mui-disabled': { color: '#C3CDD6', borderColor: surface.border }
              }}
            >
              <FileDownloadOutlined sx={{ fontSize: 18 }} />
            </ButtonBase>
          </Tooltip>
        </Box>
      }
    >
      <DataTable
        columns={columns}
        rows={sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)}
        rowKey={(reward, index) => `${reward.id}-${index}`}
        emptyMessage={
          search.trim()
            ? `No rewards match “${search.trim()}”.`
            : 'No rewards earned in this period.'
        }
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
      {sorted.length > 0 && (
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={sorted.length}
          onPageChange={setPage}
          noun='rewards'
        />
      )}
    </Panel>
  );
};

export default RecentRewardsTable;
